import { marked, Renderer, type Tokens } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Custom renderer with Tailwind CSS styling
 */
class CustomRenderer extends Renderer {
  // Customize link rendering
  link(token: Tokens.Link): string {
    const text = this.parser.parseInline(token.tokens);
    const titleAttr = token.title ? ` title="${token.title}"` : '';
    return `<a href="${token.href}"${titleAttr} class="text-primary hover:text-primary-focus underline" target="_blank" rel="noopener noreferrer">${text}</a>`;
  }

  // Customize ordered/unordered lists with explicit styling
  list(token: Tokens.List): string {
    const type = token.ordered ? 'ol' : 'ul';
    const startAttr = token.ordered && token.start !== 1 && token.start !== '' ? ` start="${token.start}"` : '';
    const listClass = token.ordered
      ? 'list-decimal list-outside ml-6 my-2 space-y-1'
      : 'list-disc list-outside ml-6 my-2 space-y-1';

    const body = token.items.map((item) => this.listitem(item)).join('');
    return `<${type}${startAttr} class="${listClass}">${body}</${type}>`;
  }

  // Customize list item
  listitem(item: Tokens.ListItem): string {
    const text = this.parser.parse(item.tokens);
    if (item.task) {
      const checkbox = item.checked
        ? '<input type="checkbox" checked disabled class="checkbox checkbox-sm mr-2" />'
        : '<input type="checkbox" disabled class="checkbox checkbox-sm mr-2" />';
      return `<li class="pl-1 flex items-start gap-1">${checkbox}<span>${text}</span></li>`;
    }
    return `<li class="pl-1">${text}</li>`;
  }

  // Customize code blocks
  code(token: Tokens.Code): string {
    const language = token.lang || 'plaintext';
    const escapedCode = token.text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<pre class="bg-base-300 rounded-lg p-3 my-2 overflow-x-auto"><code class="language-${language} text-sm font-mono" data-language="${language}">${escapedCode}</code></pre>`;
  }

  // Customize inline code
  codespan(token: Tokens.Codespan): string {
    return `<code class="bg-base-300 px-1.5 py-0.5 rounded text-sm font-mono">${token.text}</code>`;
  }

  // Customize blockquote
  blockquote(token: Tokens.Blockquote): string {
    const body = this.parser.parse(token.tokens);
    return `<blockquote class="border-l-4 border-primary/50 pl-4 my-2 italic text-base-content/80">${body}</blockquote>`;
  }

  // Customize headings
  heading(token: Tokens.Heading): string {
    const text = this.parser.parseInline(token.tokens);
    const sizes: Record<number, string> = {
      1: 'text-2xl font-bold mt-4 mb-2',
      2: 'text-xl font-bold mt-3 mb-2',
      3: 'text-lg font-semibold mt-3 mb-1',
      4: 'text-base font-semibold mt-2 mb-1',
      5: 'text-sm font-semibold mt-2 mb-1',
      6: 'text-sm font-medium mt-2 mb-1'
    };
    return `<h${token.depth} class="${sizes[token.depth]}">${text}</h${token.depth}>`;
  }

  // Customize horizontal rule
  hr(): string {
    return `<hr class="my-4 border-base-300" />`;
  }

  // Customize paragraph
  paragraph(token: Tokens.Paragraph): string {
    const text = this.parser.parseInline(token.tokens);
    return `<p class="my-1.5 leading-relaxed">${text}</p>`;
  }

  // Customize table
  table(token: Tokens.Table): string {
    const headerHtml = token.header.map((cell) => {
      const content = this.parser.parseInline(cell.tokens);
      const alignClass = cell.align ? ` text-${cell.align}` : '';
      return `<th class="px-3 py-2 font-semibold${alignClass}">${content}</th>`;
    }).join('');

    const bodyHtml = token.rows.map((row) => {
      const cells = row.map((cell) => {
        const content = this.parser.parseInline(cell.tokens);
        const alignClass = cell.align ? ` text-${cell.align}` : '';
        return `<td class="px-3 py-2${alignClass}">${content}</td>`;
      }).join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    return `<div class="overflow-x-auto my-3"><table class="table table-sm w-full border border-base-300 rounded"><thead class="bg-base-200">${headerHtml}</thead><tbody>${bodyHtml}</tbody></table></div>`;
  }

  // Customize strong/bold
  strong(token: Tokens.Strong): string {
    const text = this.parser.parseInline(token.tokens);
    return `<strong class="font-semibold">${text}</strong>`;
  }

  // Customize emphasis/italic
  em(token: Tokens.Em): string {
    const text = this.parser.parseInline(token.tokens);
    return `<em class="italic">${text}</em>`;
  }

  // Customize strikethrough
  del(token: Tokens.Del): string {
    const text = this.parser.parseInline(token.tokens);
    return `<del class="line-through text-base-content/60">${text}</del>`;
  }

  // Customize image
  image(token: Tokens.Image): string {
    const titleAttr = token.title ? ` title="${token.title}"` : '';
    return `<img src="${token.href}" alt="${token.text}"${titleAttr} class="max-w-full h-auto rounded my-2" loading="lazy" />`;
  }

  // Line break
  br(): string {
    return '<br />';
  }
}

const renderer = new CustomRenderer();

/**
 * Configure marked for optimal rendering
 */
marked.use({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
  renderer
});

/**
 * Render markdown to sanitized HTML
 * @param markdown - The markdown string to render
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export function renderMarkdown(markdown: string): string {
  if (!markdown) return '';

  // Convert markdown to HTML
  const rawHtml = marked.parse(markdown) as string;

  // Sanitize to prevent XSS
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'hr',
      'strong', 'em', 'u', 'del', 's', 'b', 'i',
      'code', 'pre',
      'ul', 'ol', 'li',
      'a',
      'blockquote',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span',
      'img',
      'sup', 'sub',
      'mark',
      'input'
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'class', 'data-language', 'target', 'rel',
      'src', 'alt', 'loading', 'width', 'height',
      'start', 'type', 'checked', 'disabled'
    ]
  });
}
